import * as React from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import { useAuth } from '../../services/cognito';

interface ConfirmationFormProps {
  username: string;
  onConfirmationSuccess: () => void;
  onSwitchToLogin: () => void;
}

export default function ConfirmationForm({ 
  username, 
  onConfirmationSuccess, 
  onSwitchToLogin 
}: ConfirmationFormProps) {
  const [code, setCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [resendLoading, setResendLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [success, setSuccess] = React.useState('');

  const { confirmSignUp, resendConfirmationCode } = useAuth();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError('');

    try {
      await confirmSignUp(username, code);
      setSuccess('사용자명이 성공적으로 확인되었습니다!');
      setTimeout(() => {
        onConfirmationSuccess();
      }, 2000);
    } catch (err: any) {
      console.error('Confirmation error:', err);
      setError(err.message || '사용자명 확인에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError('');
    setSuccess('');

    try {
      await resendConfirmationCode(username);
      setSuccess('확인 코드를 다시 발송했습니다.');
    } catch (err: any) {
      console.error('Resend error:', err);
      setError(err.message || '코드 재발송에 실패했습니다.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        이메일 확인
      </Typography>
      
      <Typography variant="body1" align="center" sx={{ mb: 2 }}>
        <strong>{username}</strong>으로 발송된 확인 코드를 입력해주세요.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          id="code"
          label="확인 코드"
          name="code"
          autoComplete="one-time-code"
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={loading}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : '확인'}
        </Button>
        
        <Box sx={{ textAlign: 'center', mt: 2, mb: 2 }}>
          <Link
            component="button"
            variant="body2"
            onClick={handleResend}
            disabled={resendLoading || loading}
          >
            {resendLoading ? '발송 중...' : '코드 재발송'}
          </Link>
        </Box>
        
        <Box sx={{ textAlign: 'center' }}>
          <Link
            component="button"
            variant="body2"
            onClick={() => onSwitchToLogin()}
            disabled={loading}
          >
            로그인으로 돌아가기
          </Link>
        </Box>
      </Box>
    </Paper>
  );
}